import getMissingDefinitions from "./getMissingDefinitions";
import getUnusedPaths from "./getUnusedPaths";
import logger, { VERBOSE } from "./logger";
import subscribe from "./subscribe";
import type { UnsubsByPath } from "./types";
import {
  type DataSnapshot,
  type Unsubscribe,
} from "../../dependencies/firebase";

export default async function multiSubscribe<
  PathDefinition extends { onlyOnce?: boolean },
  Availability,
>(
  newDefinitions: PathDefinition[],
  unsubsByPath: UnsubsByPath,
  unsubOne: (path: string) => void,
  addUnsub: (path: string, unsub: Unsubscribe) => void,
  definitionToPath: (def: Omit<PathDefinition, "onlyOnce">) => string,
  getAvailabilityFromSnapshot: (
    snapshot: DataSnapshot,
    path: string,
  ) => Availability | null,
  onValue: (
    value: Omit<PathDefinition, "onlyOnce"> & {
      availability: Availability | null;
    },
  ) => Promise<void>,
  abortSignal: AbortSignal,
) {
  logger.log(
    "Subscribing to new definitions:",
    VERBOSE ? newDefinitions : newDefinitions.length,
  );
  logger.log(
    "Existing paths: ",
    VERBOSE ? Object.keys(unsubsByPath) : Object.keys(unsubsByPath).length,
  );

  const missingDefinitions = getMissingDefinitions(
    unsubsByPath,
    newDefinitions,
    definitionToPath,
  );
  logger.log(
    "Missing definitions:",
    VERBOSE ? missingDefinitions : missingDefinitions.length,
  );

  const unusedPaths = getUnusedPaths(
    unsubsByPath,
    newDefinitions,
    definitionToPath,
  );
  logger.log("Unused paths:", VERBOSE ? unusedPaths : unusedPaths.length);

  // wait for all the restaurants to get a response from Firebase (even if empty)
  await Promise.all([
    // subscribe to the missing paths
    ...missingDefinitions.map(({ onlyOnce, ...pathDefinition }) =>
      subscribe(
        pathDefinition,
        unsubOne,
        addUnsub,
        definitionToPath,
        getAvailabilityFromSnapshot,
        onValue,
        abortSignal,
        onlyOnce,
      ),
    ),
    // unsubscribe from the unused paths
    ...unusedPaths.map(unsubOne),
  ]);

  if (abortSignal.aborted) {
    return;
  }

  logger.log("Done", VERBOSE ? newDefinitions : `(${newDefinitions.length})`);
}
